import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { allRoutes } from '../constants/config';

const { OPPORTUNITY_ZONE_GENRES } = { ...allRoutes };

/**
 * Genre Options Redux Slice
 *
 * Provides the standardized genre list backing the Prototype wizard's genre
 * dropdown (and any future genre pickers). Backed by the
 * OpportunityZone controller's genres endpoint.
 *
 * History: these exports (state.genreOptions/genreOptionsLoading, fetchGenres,
 * selectGenreOptions, selectGenreOptionsLoading) previously lived inside
 * `litGroupAutomationSlice` as a convenience. During the F3a removal pass
 * (see F3A_REMOVAL_PLAN.md Step 1) they were extracted here so the Prototype
 * wizard keeps working after the LitGroup Automation Worksheet is deleted.
 */

// ============================================================
// Initial State
// ============================================================
const initialState = {
  genreOptions: [],
  genreOptionsLoading: false,
  genreOptionsError: null,
};

// ============================================================
// Async Thunk
// ============================================================

/**
 * Fetch standardized genre options from the OpportunityZone controller.
 * Uses GET /api/opportunities/zone/filters/genres
 */
export const fetchGenres = createAsyncThunk(
  'genreOptions/fetchGenres',
  async (_, { rejectWithValue }) => {
    try {
      console.log('[GenreOptions] Fetching genres from:', OPPORTUNITY_ZONE_GENRES);
      const response = await fetch(OPPORTUNITY_ZONE_GENRES);
      if (!response.ok) {
        throw new Error(`Genre fetch failed with status ${response.status}`);
      }
      const data = await response.json();
      console.log('[GenreOptions] Genres received:', data?.length || 0, 'items');
      // Response is an array of genre strings, e.g. ["Fiction", "Mystery", ...]
      return data;
    } catch (error) {
      console.error('[GenreOptions] fetchGenres error:', error);
      return rejectWithValue(error.message || 'Failed to fetch genres');
    }
  }
);

// ============================================================
// Slice
// ============================================================
const genreOptionsSlice = createSlice({
  name: 'genreOptions',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchGenres.pending, (state) => {
        state.genreOptionsLoading = true;
        state.genreOptionsError = null;
      })
      .addCase(fetchGenres.fulfilled, (state, action) => {
        state.genreOptions = action.payload || [];
        state.genreOptionsLoading = false;
      })
      .addCase(fetchGenres.rejected, (state, action) => {
        state.genreOptionsLoading = false;
        state.genreOptionsError = action.payload || 'Failed to fetch genres';
      });
  },
});

// ============================================================
// Selectors
// ============================================================
// Stable empty-array fallback — a fresh `[]` per call makes useSelector warn
// ("returned a different result … same parameters") and rerender needlessly.
const EMPTY_ARRAY = Object.freeze([]);
export const selectGenreOptions = (state) => state.genreOptions?.genreOptions || EMPTY_ARRAY;
export const selectGenreOptionsLoading = (state) => state.genreOptions?.genreOptionsLoading || false;
export const selectGenreOptionsError = (state) => state.genreOptions?.genreOptionsError || null;

export default genreOptionsSlice.reducer;
